import React, { FC, useContext } from 'react';
import {
  blackCrossImage,
  productImage,
  redAlertIcon,
  resideoBlackLogo,
  comfortControl,
} from 'assets/images';
import { Box, Image, Heading, Text, Flex } from '@resideo/blueprint-react';
import styled from 'styled-components';
import { btnMinusBlack, btnPlusBlack } from '../../assets/images';
import { useTranslation } from 'react-i18next';
import { CartContext } from 'routes/ServicesAndSubscriptions/CartProvider';
import { convertMoneyAmount } from 'routes/ServicesAndSubscriptions/Cart';
import ServiceIconLarge from 'assets/images/ServiceIconLarge';
import {
  resideoProAircycleLink,
  resideoProHomeownerAppBrandingCompleteSetupLink,
} from 'config/externalLinks';

const Container = styled(Flex)`
  align-items: center;
  position: relative;
  .product-img {
    object-fit: cover;
  }
  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const StyledCursorImage = styled(Image)`
  cursor: pointer;
  &.closeImage {
    position: absolute;
    top: 2rem;
    right: -0.875rem;
    height: 2rem;
    width: 2rem;
  }
`;

const StyledCursorImageClose = styled(Image)`
  cursor: pointer;
  &.closeImage {
    position: absolute;
    top: 2rem;
    right: -0.875rem;
    height: 2rem;
    width: 2rem;
  }
  @media (max-width: 991px) {
    display: none;
  }
`;

const StyledCursorImageCloseMobile = styled(Image)`
  cursor: pointer;
  display: none;
  @media (max-width: 991px) {
    display: flex;
    position: absolute;
    top: -2rem;
    right: 0;
    height: 25px;
    width: 25px;
  }
`;

interface Link {
  externalLink?: boolean;
}

const StyledLink = styled.a<Link>`
  color: #00689a;
  font-weight: 500;
`;

const BoxItemDesc = styled(Box)`
  padding: 2rem;
  @media (max-width: 768px) {
    padding: 0 0 2rem 2rem;
  }
`;

const FlexProductImage = styled(Flex)`
  align-items: center;
`;

const QtyAndTotal = styled(Heading)`
  font-family: Roboto;
  font-weight: bold;
  font-size: 1.3125rem;
  line-height: 1.360625rem;
  text-align: right;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  padding-right: ${({ theme }) => theme.space.medium};
`;

const CartItem: FC<any> = ({
  setHasEnrollmentAlready,
  isAlert,
  item,
  fromThankYou,
}) => {
  const { t } = useTranslation();
  const { cartActions } = useContext(CartContext);

  const updateQuantity = type => {
    const tempData = item;
    tempData.quantity =
      type === 'add' ? tempData.quantity + 1 : tempData.quantity - 1;

    if (tempData.quantity < 1) {
      cartActions.removeCartItem(tempData.sku);
    } else {
      cartActions.updateCart(tempData);
    }
  };

  return (
    <Container>
      <FlexProductImage
        width={['10rem', '12.5rem', '12.5rem', '12.5rem']}
        minHeight='115px'
        paddingY={['15px', '20px', '20px', '20px']}
        paddingX={['10px', '20px', '20px', '20px']}
        justifyContent='center'
        style={{ border: '2px solid #ededed' }}>
        {item.type === 'product' ? (
          <Image
            src={productImage}
            alt='box image'
            maxWidth='115px'
            height='115px'
          />
        ) : item.type === 'refresh' ? (
          <Image src={comfortControl} alt='box image' height='80%' />
        ) : (
          <ServiceIconLarge />
        )}
      </FlexProductImage>
      <Flex
        flexWrap='nowrap'
        alignItems={['flex-start', 'flex-start', 'flex-start', 'center']}
        flexDirection={['column', 'column', 'column', 'row']}
        width='84%'>
        <Image
          src={resideoBlackLogo}
          alt='logo'
          display={['none', 'none', 'none', 'block']}
          paddingX={[0, 0, 0, 'large']}
          style={{ transform: 'scale(0.6)' }}
        />

        <BoxItemDesc display='block' padding='large' flex='2'>
          <Heading
            data-test-cart-item-name
            letterSpacing='0'
            fontSize='medium'
            color='cartTextHeading'>
            {item.name
              ?.split(' ')
              ?.map(w => w[0].toUpperCase() + w.substr(1).toLowerCase())
              ?.join(' ')}
          </Heading>
          {item.type === 'subscription' && (
            <Heading
              letterSpacing='0'
              fontSize='medium'
              color='cartTextHeading'>
              {item.recurringPricePerDevice
                ? convertMoneyAmount(
                    {
                      amount: item.recurringPricePerDevice,
                      currencyCode: 'USD',
                    },
                    'en-us'
                  )
                : convertMoneyAmount(
                    { amount: item.orderPrice, currencyCode: 'USD' },
                    'en-us'
                  )}{' '}
              {t('cart.per')}
              {item.month ? 'month' : 'year'} {t('cart.plusTaxes')}
            </Heading>
          )}
          {item.type === 'refresh' && (
            <Text
              fontSize='small'
              fontWeight='500'
              paddingTop='small'
              paddingBottom='small'>
              Device ID #{item.id}
            </Text>
          )}
          {item.type === 'HAB' && (
            <Heading
              letterSpacing='0'
              fontSize='medium'
              color='cartTextHeading'>
              {t('cart.houseHoldTaxes')}
            </Heading>
          )}
          {item.type === 'AirCycle_device_subscription' && (
            <Heading
              letterSpacing='0'
              fontSize='medium'
              color='cartTextHeading'>
              {t('cart.deviceId')}: {item.deviceNumber}
            </Heading>
          )}
          <Text
            data-test-cart-item-sku
            color='gray'
            fontSize='small'
            fontWeight='500'
            paddingTop='xSmall'>
            {item.sku}
          </Text>
          {item.type === 'subscription' && (
            <Text
              data-test-cart-item-sku
              color='black'
              fontSize='small'
              paddingTop='xSmall'>
              {t('cart.activationChargesLabel')}
            </Text>
          )}
          {item.type === 'HAB' && (
            <Text
              data-test-cart-item-sku
              fontSize='0.875rem'
              fontWeight='400'
              paddingTop='xSmall'>
              {t('cart.activationServiceLabel')}
            </Text>
          )}
          {isAlert && (
            <Flex alignItems='center' paddingY='0.625rem'>
              <Image src={redAlertIcon} alt='error' />
              <Text
                paddingLeft='0.313rem'
                data-test-cart-item-sku
                color='redAlertError'
                fontWeight='500'
                fontSize='0.875rem'>
                {t('cart.existingService')}
              </Text>
            </Flex>
          )}
        </BoxItemDesc>
        {fromThankYou && item.type === 'subscription' && (
          <StyledLink
            data-test-go-back-btn
            target='blank'
            href={resideoProAircycleLink}
            className='back-arrow-animation'>
            {t('cart.orderComplete.goToAirCycleDashboard')}
          </StyledLink>
        )}
        {fromThankYou && item.type === 'HAB' && (
          <StyledLink
            target='_blank'
            style={{
              textDecoration: 'none',
            }}
            href={resideoProHomeownerAppBrandingCompleteSetupLink}>
            {t('cart.completeOrder.activate')}
          </StyledLink>
        )}
        {fromThankYou && (
          <QtyAndTotal
            data-test-cart-item-qty
            fontSize='medium'
            padding='medium'
            paddingLeft={['large', 'large', 'large', '']}
            fontWeight='bold'>
            x{item.quantity || 1}
          </QtyAndTotal>
        )}
        <Flex paddingLeft={['large', 'large', 'large', '']}>
          <Flex
            display={['block', 'block', 'block', 'flex']}
            paddingRight='xLarge'>
            {!fromThankYou && (
              <>
                {item.type === 'product' ? (
                  <Box display='flex' alignItems='center'>
                    <StyledCursorImage
                      onClick={() => updateQuantity('minus')}
                      src={btnMinusBlack}
                      alt='minus'
                    />
                    <Heading
                      data-test-cart-item-qty
                      fontSize='1.3125rem'
                      padding='medium'
                      color='cartTextHeading'
                      fontWeight='bold'>
                      {item.quantity || 1}
                    </Heading>
                    <StyledCursorImage
                      onClick={() => updateQuantity('add')}
                      src={btnPlusBlack}
                      alt='minus'
                    />
                  </Box>
                ) : (
                  <Box display='flex' alignItems='center'>
                    <Heading
                      data-test-cart-item-qty
                      fontSize='1.3125rem'
                      color='cartTextHeading'
                      padding='medium'
                      paddingRight='xLarge'
                      fontWeight='bold'>
                      x{item.quantity || 1}
                    </Heading>
                  </Box>
                )}
              </>
            )}
            <QtyAndTotal
              data-test-cart-item-total
              padding={0}
              textAlign={['left', 'left', 'left', 'center']}>
              {convertMoneyAmount(
                {
                  amount:
                    (item.price ??
                      item.orderPrice ??
                      item.recurringPricePerDevice ??
                      0) * (item.quantity || 1),
                  currencyCode: item?.amount?.currencyCode ?? 'USD',
                },
                'en-us'
              )}
            </QtyAndTotal>
          </Flex>
          {!fromThankYou && (
            <StyledCursorImageClose
              src={blackCrossImage}
              alt='close'
              onClick={() => {
                setHasEnrollmentAlready(false);
                cartActions.removeCartItem(item.sku);
              }}
            />
          )}
        </Flex>
      </Flex>
      {!fromThankYou && (
        <StyledCursorImageCloseMobile
          src={blackCrossImage}
          alt='close'
          onClick={() => cartActions.removeCartItem(item.sku)}
        />
      )}
    </Container>
  );
};

export default CartItem;
